import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as authService from '../../services/authService';

const STORAGE_KEY = 'simimovies.auth.v1';

function loadAuth() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed?.token || !parsed?.user) return { token: '', user: null };
    return { token: String(parsed.token), user: parsed.user };
  } catch {
    return { token: '', user: null };
  }
}

function saveAuth(token, user) {
  try {
    if (!token || !user) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, user }));
  } catch {
    // ignore storage failures
  }
}

const boot = loadAuth();

export const loginUser = createAsyncThunk('auth/loginUser', async ({ identifier, email, password }) => {
  const data = await authService.login({ identifier, email, password });
  return { token: data?.token || '', user: data?.user || null };
});

export const registerUser = createAsyncThunk(
  'auth/registerUser',
  async ({ username, displayName, email, password }) => {
    const data = await authService.register({ username, displayName, email, password });
    return { token: data?.token || '', user: data?.user || null };
  },
);

export const forgotPasswordUser = createAsyncThunk('auth/forgotPasswordUser', async ({ email }) => {
  const data = await authService.forgotPassword({ email });
  return { message: data?.message || 'If an account exists, a reset link was sent.', debugToken: data?.debugToken || '' };
});

export const hydrateMe = createAsyncThunk('auth/hydrateMe', async () => {
  const data = await authService.getMe();
  return { user: data?.user || null };
});

export const updateMyProfile = createAsyncThunk('auth/updateMyProfile', async (payload) => {
  const data = await authService.updateMe(payload);
  return { user: data?.user || null };
});

export const changeMyPasswordThunk = createAsyncThunk('auth/changeMyPasswordThunk', async (payload) => {
  const data = await authService.changeMyPassword(payload);
  return { success: Boolean(data?.success) };
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    token: boot.token,
    user: boot.user,
    isAuthenticated: Boolean(boot.token && boot.user),
    loading: false,
    error: '',
    forgotMessage: '',
    forgotDebugToken: '',
    profileMessage: '',
  },
  reducers: {
    logoutUser(state) {
      state.token = '';
      state.user = null;
      state.isAuthenticated = false;
      state.loading = false;
      state.error = '';
      saveAuth('', null);
    },
    clearAuthError(state) {
      state.error = '';
    },
    clearAuthMessages(state) {
      state.forgotMessage = '';
      state.forgotDebugToken = '';
      state.profileMessage = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isAuthenticated = Boolean(action.payload.token && action.payload.user);
        saveAuth(state.token, state.user);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Login failed';
      })
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.isAuthenticated = Boolean(action.payload.token && action.payload.user);
        saveAuth(state.token, state.user);
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Register failed';
      })
      .addCase(forgotPasswordUser.pending, (state) => {
        state.loading = true;
        state.error = '';
        state.forgotMessage = '';
        state.forgotDebugToken = '';
      })
      .addCase(forgotPasswordUser.fulfilled, (state, action) => {
        state.loading = false;
        state.forgotMessage = action.payload.message;
        state.forgotDebugToken = action.payload.debugToken;
      })
      .addCase(forgotPasswordUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Failed to submit forgot-password';
      })
      .addCase(hydrateMe.pending, (state) => {
        state.loading = true;
      })
      .addCase(hydrateMe.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.user) {
          state.user = action.payload.user;
          state.isAuthenticated = Boolean(state.token);
          saveAuth(state.token, state.user);
        } else {
          state.token = '';
          state.user = null;
          state.isAuthenticated = false;
          saveAuth('', null);
        }
      })
      .addCase(hydrateMe.rejected, (state) => {
        state.loading = false;
        state.token = '';
        state.user = null;
        state.isAuthenticated = false;
        saveAuth('', null);
      })
      .addCase(updateMyProfile.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(updateMyProfile.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.user) {
          state.user = action.payload.user;
          state.profileMessage = 'Profile updated.';
          saveAuth(state.token, state.user);
        }
      })
      .addCase(updateMyProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Failed to update profile';
      })
      .addCase(changeMyPasswordThunk.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(changeMyPasswordThunk.fulfilled, (state) => {
        state.loading = false;
        state.profileMessage = 'Password updated.';
      })
      .addCase(changeMyPasswordThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || 'Failed to update password';
      });
  },
});

export const { logoutUser, clearAuthError, clearAuthMessages } = authSlice.actions;
export default authSlice.reducer;
